import React from 'react';
import { clsx } from 'clsx';
import { ChevronUp, ChevronDown, MoreHorizontal } from 'lucide-react';
import { TableProps, TableColumn } from '../types/common';
import { Skeleton } from './Card';

/**
 * Componente Table seguindo o design system
 */
export function Table<T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  onSort,
  sortColumn,
  sortDirection = 'asc'
}: TableProps<T>) {
  
  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable || !onSort) return;
    
    const columnKey = typeof column.key === 'string' ? column.key : String(column.key);
    const newDirection = sortColumn === columnKey && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(columnKey, newDirection);
  };

  const renderCellValue = (item: T, column: TableColumn<T>) => {
    const value = typeof column.key === 'string' ? item[column.key] : item[column.key as keyof T];
    
    if (column.render) {
      return column.render(value, item);
    }
    
    // Formatação padrão para valores
    if (value === null || value === undefined) {
      return <span className="text-slate-400">—</span>;
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }
    
    if (typeof value === 'number') {
      return value.toLocaleString('pt-BR');
    }
    
    return String(value);
  };

  const renderLoadingRows = () => {
    const loadingRows = Math.min(data.length || 5, 10);
    
    return Array.from({ length: loadingRows }).map((_, rowIndex) => (
      <tr key={`loading-${rowIndex}`} className="table-row">
        {columns.map((_, colIndex) => (
          <td key={`loading-cell-${colIndex}`} className="table-cell">
            <Skeleton className="h-4" />
          </td>
        ))}
      </tr>
    ));
  };

  const SortIcon = ({ column }: { column: TableColumn<T> }) => {
    if (!column.sortable) return null;
    
    const columnKey = typeof column.key === 'string' ? column.key : String(column.key);
    const isActive = sortColumn === columnKey;
    const isAsc = isActive && sortDirection === 'asc';
    const isDesc = isActive && sortDirection === 'desc';
    
    return (
      <span className="ml-2 flex flex-col">
        <ChevronUp 
          size={12} 
          className={clsx(
            'transition-colors -mb-1',
            isAsc ? 'text-emerald-600' : 'text-slate-300'
          )} 
        />
        <ChevronDown 
          size={12} 
          className={clsx(
            'transition-colors',
            isDesc ? 'text-emerald-600' : 'text-slate-300'
          )} 
        />
      </span>
    );
  };

  if (loading && data.length === 0) {
    return (
      <div className="table-container">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                {columns.map((column, index) => (
                  <th 
                    key={index}
                    className="table-header th"
                    style={{ width: column.width }}
                  >
                    <Skeleton className="h-4" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderLoadingRows()}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <div className="overflow-x-auto">
      <table className="table">
        <thead className="table-header">
          <tr>
            {columns.map((column, index) => (
              <th 
                key={index}
                className={clsx(
                  'table-header th',
                  column.sortable && 'cursor-pointer hover:bg-slate-100 select-none'
                )}
                style={{ width: column.width }}
                onClick={() => handleSort(column)}
              >
                <div className="flex items-center justify-between">
                  <span>{column.label}</span>
                  <SortIcon column={column} />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && renderLoadingRows()}
          
          {!loading && data.length === 0 && (
            <tr>
              <td 
                colSpan={columns.length} 
                className="table-cell text-center py-12"
              >
                <div className="flex flex-col items-center">
                  <div className="text-6xl text-slate-200 mb-4">📭</div>
                  <h3 className="text-lg font-medium text-slate-700 mb-2">
                    Nenhum resultado encontrado
                  </h3>
                  <p className="text-slate-500">
                    Tente ajustar os filtros de busca
                  </p>
                </div>
              </td>
            </tr>
          )}
          
          {!loading && data.map((item, index) => (
            <tr key={index} className="table-row">
              {columns.map((column, colIndex) => (
                <td 
                  key={colIndex}
                  className="table-cell"
                  style={{ width: column.width }}
                >
                  {renderCellValue(item, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>      </div>    </div>
  );
}

// Props para ActionButton
interface ActionButtonProps {
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'edit' | 'delete' | 'view' | 'default';
  disabled?: boolean;
  tooltip?: string;
}

/**
 * Botão de ação para tabelas
 */
export const ActionButton: React.FC<ActionButtonProps> = ({
  icon,
  onClick,
  variant = 'default',
  disabled = false,
  tooltip
}) => {
  const variantClasses = {
    edit: 'btn-edit',
    delete: 'btn-delete', 
    view: 'text-slate-500 hover:text-blue-600 hover:bg-blue-50',
    default: 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'p-2 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
        variantClasses[variant]
      )}
      title={tooltip}
    >
      {icon}
    </button>
  );
};

// Props para ActionMenu  
interface ActionMenuProps {
  items: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    variant?: 'default' | 'danger';
    disabled?: boolean;
  }>;
}

/**
 * Menu de ações dropdown para tabelas
 */
export const ActionMenu: React.FC<ActionMenuProps> = ({ items }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
      >
        <MoreHorizontal size={16} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                item.onClick();
                setIsOpen(false);
              }}
              disabled={item.disabled}
              className={clsx(
                'w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                item.variant === 'danger' 
                  ? 'text-red-600 hover:bg-red-50' 
                  : 'text-slate-700'
              )}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Re-export types for convenience
export type { TableProps, TableColumn } from '../types/common';

// Componentes simples de tabela para uso direto
export const TableHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <thead className="table-header">
    {children}
  </thead>
);

export const TableRow: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => (
  <tr className={`table-row ${className}`.trim()}>
    {children}
  </tr>
);

export const TableCell: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  colSpan?: number;
}> = ({ 
  children, 
  className = '',
  colSpan 
}) => (
  <td className={`table-cell ${className}`.trim()} colSpan={colSpan}>
    {children}
  </td>
);

// Componente wrapper de tabela simples
export const SimpleTable: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="table-container">
    <div className="overflow-x-auto">
      <table className="table">
        {children}
      </table>
    </div>
  </div>
);